import type { AdminCrewReportDataCollectionChartProps } from "../../models/admin-crew-report.models";
import {
  formatAdminCrewReportReportingPercentage,
  formatAdminCrewReportVesselCount,
  getAdminCrewReportReportingPercentage,
} from "../../utils/admin-crew-report-statistics.utils";
import {
  ADMIN_CREW_REPORT_DATA_COLLECTION_DONUT_RADIUS,
  ADMIN_CREW_REPORT_REPORTING_CARD_CLASS_NAME,
  ADMIN_CREW_REPORT_REPORTING_COLORS,
  ADMIN_CREW_REPORT_STATISTICS_TITLE_CLASS_NAME,
} from "../../utils/admin-crew-report.constants";
import type { CSSProperties } from "react";

const AdminCrewReportDataCollectionChart = ({ presentation }: AdminCrewReportDataCollectionChartProps) => {
  const primaryPercentage = getAdminCrewReportReportingPercentage(presentation.primaryPercentage);
  const formatCount = (count: number) =>
    presentation.countUnit === "vessel" ? formatAdminCrewReportVesselCount(count) : String(count);
  const reportedColorStyle = {
    "--admin-crew-report-reporting-color": ADMIN_CREW_REPORT_REPORTING_COLORS.reported.color,
    "--admin-crew-report-reporting-dark-color": ADMIN_CREW_REPORT_REPORTING_COLORS.reported.darkColor,
  } as CSSProperties;
  const notReportedColorStyle = {
    "--admin-crew-report-reporting-color": ADMIN_CREW_REPORT_REPORTING_COLORS.notReported.color,
    "--admin-crew-report-reporting-dark-color": ADMIN_CREW_REPORT_REPORTING_COLORS.notReported.darkColor,
  } as CSSProperties;

  return (
    <article
      data-test="admin-crew-report-data-collection-card"
      data-testid="admin-crew-report-data-collection-card"
      className={ADMIN_CREW_REPORT_REPORTING_CARD_CLASS_NAME}
    >
      <h3 className={ADMIN_CREW_REPORT_STATISTICS_TITLE_CLASS_NAME}>{presentation.chartTitle}</h3>
      {presentation.totalRunningVesselCount === 0 ? (
        <p className="flex flex-1 items-center justify-center px-2 text-center text-xs text-[#65706F] dark:text-gray-400">
          No running vessels are available.
        </p>
      ) : (
        <div
          role="img"
          aria-label={presentation.accessibleSummary}
          className="mt-2 flex min-h-0 flex-1 flex-col items-center justify-center"
        >
          <div className="relative h-[152px] w-[152px] flex-none" data-testid="admin-crew-report-data-collection-donut">
            <svg className="h-full w-full" viewBox="0 0 120 120" aria-hidden="true">
              <circle
                cx="60"
                cy="60"
                r={ADMIN_CREW_REPORT_DATA_COLLECTION_DONUT_RADIUS}
                fill="none"
                className="stroke-[var(--admin-crew-report-reporting-color)] dark:stroke-[var(--admin-crew-report-reporting-dark-color)]"
                style={notReportedColorStyle}
                strokeWidth="20"
              />
              {primaryPercentage > 0 && (
                <circle
                  cx="60"
                  cy="60"
                  r={ADMIN_CREW_REPORT_DATA_COLLECTION_DONUT_RADIUS}
                  fill="none"
                  pathLength="100"
                  className="stroke-[var(--admin-crew-report-reporting-color)] dark:stroke-[var(--admin-crew-report-reporting-dark-color)]"
                  style={reportedColorStyle}
                  strokeDasharray={`${primaryPercentage} ${100 - primaryPercentage}`}
                  strokeWidth="20"
                  transform="rotate(-90 60 60)"
                  data-testid="admin-crew-report-data-collection-donut-reported"
                />
              )}
            </svg>
            <div aria-hidden="true" className="absolute inset-0 flex flex-col items-center justify-center text-center">
              <span className="text-lg font-semibold tabular-nums leading-none text-[#24302E] dark:text-gray-100">
                {formatAdminCrewReportReportingPercentage(presentation.primaryPercentage)}
              </span>
              <span className="mt-1 text-[9px] font-semibold uppercase leading-none text-[#65706F] dark:text-gray-400">
                {presentation.primaryPercentageLabel}
              </span>
            </div>
          </div>
          <ul aria-hidden="true" className="mt-2 grid w-full grid-cols-1 gap-1 text-[10px] leading-3 sm:text-[11px]">
            <li className="flex min-w-0 items-center justify-between gap-2">
              <span className="flex min-w-0 items-center gap-1.5 text-[#4A5655] dark:text-gray-300">
                <span
                  className="h-2 w-2 flex-none rounded-full bg-[var(--admin-crew-report-reporting-color)] dark:bg-[var(--admin-crew-report-reporting-dark-color)]"
                  style={reportedColorStyle}
                  data-testid="admin-crew-report-data-collection-legend-reported"
                />
                <span>{presentation.primaryLegendLabel}</span>
              </span>
              <span className="whitespace-nowrap font-semibold tabular-nums text-[#24302E] dark:text-gray-100">
                {formatCount(presentation.primaryVesselCount)} (
                {formatAdminCrewReportReportingPercentage(presentation.primaryPercentage)})
              </span>
            </li>
            <li className="flex min-w-0 items-center justify-between gap-2">
              <span className="flex min-w-0 items-center gap-1.5 text-[#4A5655] dark:text-gray-300">
                <span
                  className="h-2 w-2 flex-none rounded-full bg-[var(--admin-crew-report-reporting-color)] dark:bg-[var(--admin-crew-report-reporting-dark-color)]"
                  style={notReportedColorStyle}
                  data-testid="admin-crew-report-data-collection-legend-not-reported"
                />
                <span>{presentation.secondaryLegendLabel}</span>
              </span>
              <span className="whitespace-nowrap font-semibold tabular-nums text-[#24302E] dark:text-gray-100">
                {formatCount(presentation.secondaryVesselCount)} (
                {formatAdminCrewReportReportingPercentage(presentation.secondaryPercentage)})
              </span>
            </li>
          </ul>
        </div>
      )}
    </article>
  );
};

export default AdminCrewReportDataCollectionChart;
